export const normalizeStatesResponse = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.states)
      ? payload.states
      : [];

  return list
    .map((item) => {
      const value =
        item.code ??
        item.state_code ??
        item.abbreviation ??
        item.iso ??
        item.id ??
        item.value;
      const label =
        item.name ?? item.state ?? item.title ?? item.code ?? item.abbreviation ?? item.iso;
      if (!value || !label) return null;
      return { value: String(value), label: String(label) };
    })
    .filter(Boolean);
};

export const normalizeCountryCode = (code) => String(code || "").trim().toUpperCase();

// Resolve the visitor's ISO2 country by IP. Tries the Cloudflare-backed
// endpoint first, falls back to the generic ip-details lookup. Returns the
// normalized ISO2 code as a string, or "" when the lookup fails (so callers
// can treat an empty result as "global / no targeting").
export const fetchIpCountry = async (api) => {
  try {
    const resp = await api.get("/cf-ip-country");
    if (resp.status === 200) return normalizeCountryCode(resp.data?.country);
  } catch (_) {
    try {
      const resp = await api.get("/ip-details");
      if (resp.status === 200) return normalizeCountryCode(resp.data?.country);
    } catch (_) {
      // ignore — lookup failed
    }
  }
  return "";
};

// Geo-targeting for CMS records. The admin stores a `countries` field as a JSON
// array of ISO2 codes, e.g. ["CA","AU"]. Empty / missing / malformed = global
// (show to everyone). A non-empty list shows the record only to visitors whose
// resolved country is in the list. When the visitor country is unknown (IP
// lookup failed -> ""), everything is shown (global fallback) so geo-targeting
// can never hide content due to a failed lookup.
export const isVisibleInCountry = (rawCountries, visitorCountry) => {
  // Parse the stored value into an array of normalized ISO2 codes.
  let list = [];
  if (Array.isArray(rawCountries)) {
    list = rawCountries;
  } else if (typeof rawCountries === "string" && rawCountries.trim() !== "") {
    try {
      const parsed = JSON.parse(rawCountries);
      if (Array.isArray(parsed)) list = parsed;
    } catch (_) {
      // malformed -> treat as global
      return true;
    }
  }

  const codes = list
    .map((c) => normalizeCountryCode(c))
    .filter((c) => c !== "");

  // Empty target list = global.
  if (codes.length === 0) return true;

  // Unknown visitor country = global fallback (never hide on lookup failure).
  const visitor = normalizeCountryCode(visitorCountry);
  if (visitor === "") return true;

  return codes.includes(visitor);
};

// Filter a list of CMS records by the visitor's country, reading each record's
// `countries` field. Records with empty/global targeting always pass through.
export const filterByCountry = (records, visitorCountry) =>
  (Array.isArray(records) ? records : []).filter((record) =>
    isVisibleInCountry(record?.countries, visitorCountry)
  );
